import { set } from 'lodash';
import type { CustomControlCode } from '@common/enums';
import { never } from '@common/utils/never';
import type { InitialValue, InputField, Section } from './formEngine.types';
import { getInputFieldsFromSections } from './typedSections';

export function getInitialCheckboxGroupValue(checkboxEnumType: Record<string, string>) {
    return Object.fromEntries(Object.keys(checkboxEnumType).map((key) => [key, null]));
}

export function getInitialValue<
    Data extends Record<string, unknown>,
    TCustomControlCode extends CustomControlCode | undefined,
    Version extends number,
>(field: InputField<Data, TCustomControlCode, Version>): InitialValue {
    if ('initialValue' in field) {
        return field.initialValue;
    }

    const { type } = field;
    switch (type) {
        case 'assets': {
            return [];
        }
        case 'boolean':
        case 'dateInput':
        case 'icd10':
        case 'monthInput':
        case 'number':
        case 'radioGroup':
        case 'select':
        case 'textArea':
        case 'textInput':
            return null;
        case 'checkboxGroup':
        case 'nestedCheckboxGroup':
            return getInitialCheckboxGroupValue(field.checkboxEnumType);
        case 'custom':
            throw new Error(`No initial data for custom type and key "${field.key}"`);
        case 'quantity':
            return {
                unit: field.defaultUnit ?? null,
                value: null,
            };
        /* c8 ignore start */
        default:
            never(type);
        /* c8 ignore stop */
    }
}

export function createInitialData<
    Data extends Record<string, unknown>,
    ContentKey extends string | undefined = undefined,
    TCustomControlCode extends CustomControlCode | undefined = undefined,
    Version extends number = 1,
>(sections: Section<Data, ContentKey, TCustomControlCode, Version>[]): Data {
    return getInputFieldsFromSections<Data, ContentKey, TCustomControlCode, Version>(sections).reduce(
        (object, field) => {
            // TODO: CS-24959 Custom schema to make assets work with multiple category fields
            if (field.type === 'assets' && field.key !== 'assets' && !object['assets'] && field.category) {
                set(object, 'assets', []);
            }

            set(object, field.key, getInitialValue(field));

            return object;
        },
        {} as Data
    );
}
